import { PackageRepository } from '../../domain/repositories/PackageRepository'
import { PackagesService } from '@/backend/market'
import type { PackageFilterEntity } from '../../domain/entities/PackageFilterEntity'
import { PackageFilterModel } from '../models/PackageFilterModel'
import { PaginateEntity } from '@/shared/entities/PaginateEntity'
import { PackageMiniModel } from '../models/PackageMiniModel'
import type { PackageMiniEntity } from '../../domain/entities/PackageMiniEntity'
import type { PackageEntity } from '../../domain/entities/PackageEntity'
import { PackageModel } from '../models/PackageModel'
import type { PackageStatusLogEntity } from '../../domain/entities/PackageStatusLogEntity'
import { PackageStatusLogModel } from '../models/PackageStatusLogModel'
import { PackageStatus } from '../../domain/enums/PackageStatus'
import { useToastStore } from '@/shared/stores/useToastStore'
import { CargosService } from '@/backend/market'
import type { PutPackageAddressRequestAdmin } from '@/backend/market'

export class PackageRepositoryImpl implements PackageRepository {
  async getPackages(
    filterData: PackageFilterEntity
  ): Promise<PaginateEntity<PackageMiniEntity[]> | { error: true }> {
    const packageFilterModel = new PackageFilterModel(filterData)
    try {
      const response = await PackagesService.getPackagesAdmin(packageFilterModel)
      const packageModels = response.items?.map((item) => new PackageMiniModel(item))
      const packageResponse = new PaginateEntity<PackageMiniModel[]>(response, packageModels)

      return packageResponse
    } catch {
      return { error: true }
    }
  }
  async getPackage(id: string): Promise<PackageEntity | { error: true }> {
    try {
      const response = await PackagesService.getPackageAdmin({ id })
      const packageModel = new PackageModel(response)
      return packageModel
    } catch {
      return { error: true }
    }
  }
  async getPackageStatusLogs(id: string): Promise<PackageStatusLogEntity[] | { error: true }> {
    try {
      const response = await PackagesService.getPackageStatusLogsAdmin({ id })
      const packageStatusLogsModel = response.statusLogs?.map(
        (item) => new PackageStatusLogModel(item)
      )
      return packageStatusLogsModel ?? []
    } catch {
      return { error: true }
    }
  }
  async updatePackageStatus(
    packageId: string,
    status?: PackageStatus,
    cancelPackageReasonId?: number,
    driverId?: number,
    assignDriver?: boolean
  ): Promise<{ success: true } | { error: true }> {
    if (assignDriver) {
      await PackagesService.postPackageAssignAdmin({
        id: packageId,
        requestBody: {
          driverId
        }
      })
      return { success: true }
    }
    try {
      const toastStore = useToastStore()

      switch (status) {
        case PackageStatus.APPROVED:
          await PackagesService.postPackageApproveAdmin({
            id: packageId
          })
          break
       
        case PackageStatus.ACQUIRED:
          await PackagesService.postPackageAcquireAdmin({
            id: packageId,
            requestBody: {
              driverId: driverId!
            }
          })
          break
        case PackageStatus.AT_SOURCE:
          await PackagesService.postPackageAtSourceAdmin({
            id: packageId
          })
          break
        case PackageStatus.PICKED_UP:
          await PackagesService.postPackagePickedUpAdmin({
            id: packageId
          })
          break
        case PackageStatus.DELIVERING:
          await PackagesService.postPackageDeliveringAdmin({
            id: packageId
          })
          break
        case PackageStatus.AT_DESTINATION:
          await PackagesService.postPackageAtDestinationAdmin({
            id: packageId
          })
          break
        case PackageStatus.COMPLETED:
          await PackagesService.postPackageCompleteAdmin({
            id: packageId
          })
          break
        case PackageStatus.NOT_DELIVERED:
          await PackagesService.postPackageNotDeliveredAdmin({
            id: packageId
          })
          break
        case PackageStatus.CANCELED:
          await PackagesService.postPackageCancelAdmin({
            id: packageId,
            requestBody: {
              cancelPackageReasonId: cancelPackageReasonId!
            }
          })
          break
        default:
          toastStore.updateToastState({
            severity: 'error',
            summary: 'Error',
            detail: 'Invalid status'
          })
          return { error: true }
      }
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async unAssignPackageDriver(packageId: string): Promise<{ success: true } | { error: true }> {
    try {
      await PackagesService.postOrderUnassignAdmin({ id: packageId })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async cancelDeliveryPackage(
    packageId: string,
    cancelPackageReasonId: number
  ): Promise<{ success: true } | { error: true }> {
    try {
      await PackagesService.postPackageCancelAdmin({
        id: packageId,
        requestBody: {
          cancelPackageReasonId: cancelPackageReasonId!
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async cancelCargo(
    cargoId: string,
    cancelCargoReasonId: number
  ): Promise<{ success: true } | { error: true }> {
    try {
      await CargosService.postCancelCargoAdmin({
        id: cargoId,
        requestBody: {
          cancelCargoReasonId
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async transferCargoAndAcquire(
    cargoId: string,
    driverId: number,
    cancelCargoReasonId: number
  ): Promise<{ success: true } | { error: true }> {
    try {
      await CargosService.postTransferCargoAndAcquireAdmin({
        id: cargoId,
        requestBody: {
          driverId,
          cancelCargoReasonId
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async transferCargoAndAssign(
    cargoId: string,
    driverId: number,
    cancelCargoReasonId: number
  ): Promise<{ success: true } | { error: true }> {
    try {
      await CargosService.postTransferCargoAndAssignAdmin({
        id: cargoId,
        requestBody: {
          driverId,
          cancelCargoReasonId
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async updatePackageAddress(
    packageId: string,
    requestBody: PutPackageAddressRequestAdmin
  ): Promise<{ success: true } | { error: true }> {
    try {
      await PackagesService.putPackageAddressAdmin({
        id: packageId,
        requestBody
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
}
